import React, { useState } from "react";
import api from "../services/api";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  TextField,
  Autocomplete,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Alert,
  Chip,
  CircularProgress,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DownloadIcon from "@mui/icons-material/Download";

const fmt = (n) => Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 });

const statusChip = (s) => {
  const map = {
    matched:    { label: "Matched", color: "success" },
    ambiguous:  { label: "Pick one", color: "warning" },
    unknown:    { label: "Not found", color: "error" },
    missing:    { label: "Missing", color: "error" },
    new:        { label: "New",     color: "info" },
  };
  const m = map[s] || { label: s, color: "default" };
  return <Chip size="small" label={m.label} color={m.color} />;
};

export default function SaleInvoiceImport({ open, onClose, onImported }) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState("");

  // Resolution state — drives the commit body
  const [customer, setCustomer] = useState(null);     // { id, SubAccount_Name }
  const [product, setProduct] = useState(null);       // { Item_ID, Product_Description }
  const [saveCustAlias, setSaveCustAlias] = useState(true);
  const [saveProdAlias, setSaveProdAlias] = useState(true);

  const [allProducts, setAllProducts] = useState([]);
  const [allCustomers, setAllCustomers] = useState([]);

  const reset = () => {
    setFile(null); setPreview(null); setError("");
    setCustomer(null); setProduct(null);
  };

  const downloadTemplate = async () => {
    try {
      const res = await api.get("/import/sale/template", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "SaleInvoice_Template.xlsx";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError("Failed to download template");
    }
  };

  const downloadErrorReport = async () => {
    if (!preview?.lines?.length) return;
    try {
      const res = await api.post(
        "/import/errors/export",
        { kind: "sale", lines: preview.lines },
        { responseType: "blob" }
      );
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "SaleInvoice_Errors.xlsx";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e?.response?.data?.error || "No errors to export");
    }
  };

  const upload = async () => {
    if (!file) return;
    setBusy(true); setError(""); setPreview(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/import/sale/preview", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(res.data);

      // pre-fill resolutions if backend already matched them
      if (res.data?.header?.product?.status === "matched") {
        setProduct(res.data.header.product.item);
      }
      if (res.data?.header?.customer?.status === "matched") {
        setCustomer(res.data.header.customer.item);
      }

      // load full lists for fallback dropdowns
      const [p, c] = await Promise.all([
        api.get("/products"),
        api.get("/accounts"),
      ]);
      setAllProducts(p.data || []);
      setAllCustomers(c.data || []);
    } catch (e) {
      setError(e?.response?.data?.error || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  const commit = async () => {
    if (!preview) return;
    if (!customer?.id || !product?.Item_ID) {
      setError("Please pick both Customer and Product before saving.");
      return;
    }
    setBusy(true); setError("");
    try {
      const body = {
        header: {
          Customer_ID:     customer.id,
          Name:            customer.SubAccount_Name,
          Item_ID:         product.Item_ID,
          Item_Description:product.Product_Description,
          PO:              preview.header.poNumber,
          Booking:         preview.header.bookingNo,
          Delivery_Terms:  preview.header.deliveryTerms,
          Date:            preview.header.invoiceDate,
          saveCustomerAlias: saveCustAlias && preview.header.customerName !== customer.SubAccount_Name
                              ? preview.header.customerName : null,
          saveProductAlias:  saveProdAlias && preview.header.itemDesc !== product.Product_Description
                              ? preview.header.itemDesc : null,
        },
        lines: preview.lines,
      };
      const res = await api.post("/import/sale/commit", body);
      onImported?.(res.data?.Master_ID);
      reset();
      onClose();
    } catch (e) {
      setError(e?.response?.data?.error || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => { reset(); onClose(); }} fullWidth maxWidth="lg">
      <DialogTitle>Import Sale Invoice from Excel</DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {/* ============ STEP 1: file pick ============ */}
        {!preview && (
          <Box>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Use the template so the parser finds every field. Header lines are matched
              by label ("Customer", "Booking", "PO", "Item description"), and detail rows
              must include Container No, Net Wt, and Unit Price columns.
            </Typography>

            <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
              <Button
                startIcon={<DownloadIcon />}
                variant="outlined"
                onClick={downloadTemplate}
              >
                Download Template
              </Button>

              <Button component="label" variant="contained" startIcon={<UploadFileIcon />}>
                Choose .xlsx
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  hidden
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                />
              </Button>

              {file && <Typography variant="body2">{file.name}</Typography>}

              <Button
                variant="contained"
                color="primary"
                disabled={!file || busy}
                onClick={upload}
              >
                {busy ? <CircularProgress size={20} /> : "Parse & Preview"}
              </Button>
            </Box>
          </Box>
        )}

        {/* ============ STEP 2: preview + resolve ============ */}
        {preview && (
          <Box>
            <Typography variant="subtitle1" fontWeight="bold" mb={1}>Header</Typography>
            <Table size="small" sx={{ mb: 2 }}>
              <TableBody>
                <TableRow>
                  <TableCell sx={{ width: 180 }}>Customer (file)</TableCell>
                  <TableCell>{preview.header.customerName || "—"} {statusChip(preview.header.customer.status)}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Customer (resolved)</TableCell>
                  <TableCell>
                    <Autocomplete
                      size="small"
                      options={allCustomers}
                      getOptionLabel={(o) => o?.SubAccount_Name || ""}
                      value={customer}
                      onChange={(_, v) => setCustomer(v)}
                      renderInput={(p) => <TextField {...p} placeholder="Pick customer" />}
                      sx={{ width: 360 }}
                    />
                    {preview.header.customer.status !== "matched" && customer && (
                      <FormControlLabel
                        sx={{ ml: 1 }}
                        control={
                          <Checkbox
                            size="small"
                            checked={saveCustAlias}
                            onChange={(e) => setSaveCustAlias(e.target.checked)}
                          />
                        }
                        label={`Save "${preview.header.customerName}" as alias`}
                      />
                    )}
                  </TableCell>
                </TableRow>

                <TableRow>
                  <TableCell>Product (file)</TableCell>
                  <TableCell>{preview.header.itemDesc || "—"} {statusChip(preview.header.product.status)}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Product (resolved)</TableCell>
                  <TableCell>
                    <Autocomplete
                      size="small"
                      options={allProducts}
                      getOptionLabel={(o) => o?.Product_Description || ""}
                      value={product}
                      onChange={(_, v) => setProduct(v)}
                      renderInput={(p) => <TextField {...p} placeholder="Pick product" />}
                      sx={{ width: 360 }}
                    />
                    {preview.header.product.status !== "matched" && product && (
                      <FormControlLabel
                        sx={{ ml: 1 }}
                        control={
                          <Checkbox
                            size="small"
                            checked={saveProdAlias}
                            onChange={(e) => setSaveProdAlias(e.target.checked)}
                          />
                        }
                        label={`Save "${preview.header.itemDesc}" as alias`}
                      />
                    )}
                  </TableCell>
                </TableRow>

                <TableRow>
                  <TableCell>Booking</TableCell>
                  <TableCell>
                    {preview.header.bookingNo || "—"} {statusChip(preview.header.booking.status)}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>PO</TableCell>
                  <TableCell>{preview.header.poNumber || "—"}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>{preview.header.invoiceDate || "—"}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Delivery Terms</TableCell>
                  <TableCell>{preview.header.deliveryTerms || "—"}</TableCell>
                </TableRow>
              </TableBody>
            </Table>

            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
              <Typography variant="subtitle1" fontWeight="bold">
                Container Lines ({preview.lines.length})
                {preview.summary?.errorRows > 0 && (
                  <Chip
                    size="small"
                    color="error"
                    label={`${preview.summary.errorRows} row${preview.summary.errorRows === 1 ? "" : "s"} with issues`}
                    sx={{ ml: 1 }}
                  />
                )}
              </Typography>
              {preview.summary?.errorRows > 0 && (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={downloadErrorReport}
                >
                  Download Error Report
                </Button>
              )}
            </Box>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>#</TableCell>
                  <TableCell>Container</TableCell>
                  <TableCell>Seal</TableCell>
                  <TableCell align="right">Pallets</TableCell>
                  <TableCell align="right">Gross</TableCell>
                  <TableCell align="right">Tare</TableCell>
                  <TableCell align="right">Net</TableCell>
                  <TableCell align="right">Rate</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell>Issues</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {preview.lines.map((l, i) => (
                  <TableRow key={i} sx={l._errors?.length ? { backgroundColor: "rgba(255,0,0,0.06)" } : undefined}>
                    <TableCell>{i + 1}</TableCell>
                    <TableCell>{l.Container_No}</TableCell>
                    <TableCell>{l.Seal_No}</TableCell>
                    <TableCell align="right">{l.Pallets}</TableCell>
                    <TableCell align="right">{fmt(l.G_Wt)}</TableCell>
                    <TableCell align="right">{fmt(l.T_Wt)}</TableCell>
                    <TableCell align="right">{fmt(l.N_Wt)}</TableCell>
                    <TableCell align="right">{fmt(l.Rate)}</TableCell>
                    <TableCell align="right">{fmt(l.N_Wt * l.Rate)}</TableCell>
                    <TableCell>
                      {l._errors?.length ? (
                        <Typography variant="caption" color="error">
                          {l._errors.join("; ")}
                        </Typography>
                      ) : (
                        <Chip size="small" color="success" label="OK" variant="outlined" />
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow>
                  <TableCell colSpan={6} align="right" sx={{ fontWeight: "bold" }}>Totals</TableCell>
                  <TableCell align="right" sx={{ fontWeight: "bold" }}>{fmt(preview.totals.netWt)}</TableCell>
                  <TableCell />
                  <TableCell align="right" sx={{ fontWeight: "bold" }}>${fmt(preview.totals.amount)}</TableCell>
                  <TableCell />
                </TableRow>
              </TableBody>
            </Table>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={() => { reset(); onClose(); }}>Cancel</Button>
        {preview && (
          <Button onClick={reset} disabled={busy}>Start Over</Button>
        )}
        {preview && (
          <Button
            variant="contained"
            disabled={busy || !customer?.id || !product?.Item_ID}
            onClick={commit}
          >
            {busy ? <CircularProgress size={20} /> : "Save Sale Invoice"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
